<script setup>
// Raporlar sekmesi: asenkron Meta rapor islerini calistirir ve sonuclarini
// tabloya doker.
//
// Iki yol var cunku ads_run_report_and_wait buyuk hesaplarda dakikalarca
// surebiliyor ve istek ara katmanlarda zaman asimina ugrayabiliyor. Bu
// durumda kullanici isi arka planda baslatip rapor kimligiyle durumu
// sorgulayabilsin diye ayri dugmeler birakildi.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { aracCalistir, diziyeCevir, paraBicimle, sayiBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: null },
});

const { t } = useI18n();
const route = useRoute();

const aralik = ref('last_7d');
const satirlar = ref([]);
const ozetMetni = ref('');
const raporKimligi = ref('');
const durumMetni = ref('');
const hazirlaniyor = ref(false);
const yukleniyor = ref(false);
const hata = ref('');

const accountId = computed(() => route.params.accountId);
const birim = computed(() => props.hesapAyrinti?.currency || 'TRY');

const araliklar = computed(() => [
  { deger: 'last_7d', ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.ARALIK.SON_7') },
  { deger: 'last_14d', ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.ARALIK.SON_14') },
  { deger: 'last_30d', ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.ARALIK.SON_30') },
  { deger: 'this_month', ad: t('META_ADS.ISTATISTIKLER.PERFORMANS.ARALIK.BU_AY') },
]);

// Salt tarih metnini (2024-05-01) dogrudan Date'e vermek UTC olarak cozulup
// yerel saate cevrildigi icin gunu kaydirabiliyor; saat ekleyerek okuyoruz.
const tarihBicimle = deger => {
  const metin = String(deger ?? '');
  if (!metin) return '—';
  const saltTarih = /^\d{4}-\d{2}-\d{2}$/.test(metin);
  const tarih = saltTarih ? new Date(`${metin}T00:00:00`) : new Date(metin);
  if (Number.isNaN(tarih.getTime())) return metin;
  return saltTarih
    ? tarih.toLocaleDateString('tr-TR', { dateStyle: 'medium' })
    : tarih.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
};

// Rapor sutunlari insights ile ayni alanlari dondurur; para alanlari kurus
// geldigi icin paraBicimle uzerinden geciyor.
const sutunlar = computed(() => [
  {
    anahtar: 'date_start',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.BASLANGIC'),
    bicim: tarihBicimle,
  },
  {
    anahtar: 'date_stop',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.BITIS'),
    bicim: tarihBicimle,
  },
  {
    anahtar: 'spend',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.HARCAMA'),
    bicim: deger => paraBicimle(deger, birim.value),
  },
  {
    anahtar: 'impressions',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.GOSTERIM'),
    bicim: deger => sayiBicimle(deger),
  },
  {
    anahtar: 'clicks',
    baslik: t('META_ADS.ISTATISTIKLER.PERFORMANS.SUTUN.TIKLAMA'),
    bicim: deger => sayiBicimle(deger),
  },
]);

// Rapor istenen alanlara gore bambaska sutunlarla donebilir; bilinen insights
// alanlari yoksa sutunlari AracTablosu'nun kendisi turetsin diye bos geciyoruz.
const gorunenSutunlar = computed(() => {
  const ilk = satirlar.value[0];
  if (!ilk || typeof ilk !== 'object') return [];
  const tanidik = 'spend' in ilk || 'impressions' in ilk || 'date_start' in ilk;
  return tanidik ? sutunlar.value : [];
});

// Rapor kimligi arac yanitinda farkli adlarla gelebiliyor; ilk bulunani
// aliyoruz ki kullanici kimligi elle kopyalamak zorunda kalmasin.
const kimligiCikar = sonuc => {
  const veri = sonuc?.veri;
  const kaynak = Array.isArray(veri) ? veri[0] : veri;
  return String(
    kaynak?.report_run_id || kaynak?.id || kaynak?.async_report_id || ''
  );
};

const raporCalistir = async () => {
  if (!props.hesap) return;
  hazirlaniyor.value = true;
  hata.value = '';
  ozetMetni.value = '';
  satirlar.value = [];
  try {
    const sonuc = await aracCalistir(
      accountId.value,
      'ads_run_report_and_wait',
      { account_id: props.hesap, date_preset: aralik.value }
    );
    satirlar.value = diziyeCevir(sonuc);
    ozetMetni.value = sonuc?.ozet || '';
    const kimlik = kimligiCikar(sonuc);
    if (kimlik) raporKimligi.value = kimlik;
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    hazirlaniyor.value = false;
  }
};

const arkaPlandaBaslat = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(
      accountId.value,
      'ads_create_async_report',
      { account_id: props.hesap, date_preset: aralik.value }
    );
    // Kimlik yalnizca bulunabildiyse yaziliyor: arac kimligi ozet metninde
    // dondurdugunde bos atama kullanicinin elindeki gecerli kimligi silerdi.
    const kimlik = kimligiCikar(sonuc);
    if (kimlik) raporKimligi.value = kimlik;
    durumMetni.value = sonuc?.ozet || '';
    useAlert(t('META_ADS.ISTATISTIKLER.RAPORLAR.BASLATILDI'));
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};

const durumSorgula = async () => {
  if (!raporKimligi.value) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_report_status', {
      report_run_id: raporKimligi.value,
    });
    const veri = sonuc?.veri;
    durumMetni.value =
      veri?.async_status || veri?.status || sonuc?.ozet || '';
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};

const sonuclariGetir = async () => {
  if (!raporKimligi.value) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_report_results', {
      report_run_id: raporKimligi.value,
    });
    satirlar.value = diziyeCevir(sonuc);
    ozetMetni.value = sonuc?.ozet || '';
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};

// Meta is durumunu "Job Completed" gibi ham Ingilizce metinle dondurur.
// Bilinen degerleri cevirip gosteriyoruz; taninmayan deger oldugu gibi kaliyor
// ki durum bilgisi hic kaybolmasin.
const DURUM_KARSILIKLARI = {
  'job not started': 'BASLAMADI',
  'job started': 'BASLADI',
  'job running': 'CALISIYOR',
  'job completed': 'TAMAMLANDI',
  'job failed': 'BASARISIZ',
  'job skipped': 'ATLANDI',
};

const durumEtiketi = computed(() => {
  const ham = durumMetni.value;
  const ek = DURUM_KARSILIKLARI[String(ham).trim().toLowerCase()];
  return ek ? t(`META_ADS.ISTATISTIKLER.RAPORLAR.DURUMLAR.${ek}`) : ham;
});

// Hesap degisince ekrandaki rapor temizleniyor: bu sayfa kendiliginden veri
// cekmedigi icin temizlenmezse onceki hesabin rakamlari yeni hesap secili
// gorunurken ekranda kalir ve yanlis hesaba mal edilir.
watch(
  () => props.hesap,
  () => {
    satirlar.value = [];
    ozetMetni.value = '';
    raporKimligi.value = '';
    durumMetni.value = '';
    hata.value = '';
  }
);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.HESAP_SEC') }}
    </p>

    <template v-else>
      <div class="flex flex-col gap-2">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.ISTATISTIKLER.RAPORLAR.BASLIK') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('META_ADS.ISTATISTIKLER.RAPORLAR.ZAMAN_ASIMI_UYARISI') }}
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <label class="text-sm text-n-slate-11" for="rapor-aralik">
          {{ t('META_ADS.ISTATISTIKLER.PERFORMANS.TARIH_ARALIGI') }}
        </label>
        <select
          id="rapor-aralik"
          v-model="aralik"
          class="!mb-0 !w-auto min-w-[180px]"
        >
          <option v-for="a in araliklar" :key="a.deger" :value="a.deger">
            {{ a.ad }}
          </option>
        </select>
        <Button
          size="sm"
          icon="i-lucide-play"
          :label="t('META_ADS.ISTATISTIKLER.RAPORLAR.CALISTIR')"
          :disabled="hazirlaniyor"
          @click="raporCalistir"
        />
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-clock"
          :label="t('META_ADS.ISTATISTIKLER.RAPORLAR.ARKA_PLANDA_BASLAT')"
          :disabled="hazirlaniyor"
          @click="arkaPlandaBaslat"
        />
      </div>

      <div
        v-if="hazirlaniyor"
        class="flex items-center gap-3 p-4 border rounded-lg border-n-weak"
      >
        <Spinner class="text-n-brand" />
        <span class="text-sm text-n-slate-11">
          {{ t('META_ADS.ISTATISTIKLER.RAPORLAR.HAZIRLANIYOR') }}
        </span>
      </div>

      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

      <section class="flex flex-col gap-3 pt-4 border-t border-n-weak">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.ISTATISTIKLER.RAPORLAR.KIMLIKLE_SORGULA') }}
        </h3>
        <div class="flex flex-wrap items-center gap-3">
          <input
            v-model="raporKimligi"
            type="text"
            class="!mb-0 !w-auto min-w-[240px]"
            :placeholder="t('META_ADS.ISTATISTIKLER.RAPORLAR.RAPOR_KIMLIGI')"
          />
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-search"
            :label="t('META_ADS.ISTATISTIKLER.RAPORLAR.DURUM_SORGULA')"
            :disabled="!raporKimligi || yukleniyor"
            @click="durumSorgula"
          />
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-download"
            :label="t('META_ADS.ISTATISTIKLER.RAPORLAR.SONUCLARI_GETIR')"
            :disabled="!raporKimligi || yukleniyor"
            @click="sonuclariGetir"
          />
        </div>
        <p v-if="durumMetni" class="text-sm text-n-slate-11">
          {{ t('META_ADS.ISTATISTIKLER.RAPORLAR.DURUM') }}: {{ durumEtiketi }}
        </p>
      </section>

      <section class="flex flex-col gap-2">
        <p v-if="ozetMetni" class="text-sm whitespace-pre-line text-n-slate-11">
          {{ ozetMetni }}
        </p>
        <AracTablosu
          :satirlar="satirlar"
          :sutunlar="gorunenSutunlar"
          :yukleniyor="yukleniyor || hazirlaniyor"
          :bos-metin="t('META_ADS.ISTATISTIKLER.RAPORLAR.SONUC_YOK')"
        />
      </section>
    </template>
  </div>
</template>
